import React, { type SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { type TriggerProps } from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { PopupTrigger } from '@atlassian/entry-point-popup';

import PopupComponent from '../../../../common/no-background-popup-component';
import { getSiteId } from '../../../../common/utils/ari';
import { isTeamBiDirectionalEnabled } from '../../../../common/utils/utils';
import { usePrincipalUser } from '../../../../controllers/principal-user';
import { TeamSelectorProvider } from '../../../../controllers/team-selector-context';
import { asyncTeamSelectorEntryPoint } from '../../../team-selector/entrypoint';
import { useTeamBiDirectionalExp } from '../../../team-selector/hooks/use-team-connection-exp';
import TeamSelectorSkeleton from '../../../team-selector/team-selector-skeleton';
import { type TeamData } from '../../../team-selector/types';
import { getContainerType } from '../../../team-selector/utils/getContainerType';
import { TeamButtonTrigger } from '../team-button-trigger';

export type RenderTriggerProps = {
	triggerProps: TriggerProps;
	onClick: () => void;
};

type TeamButtonComponentProps = {
	containerId: string;
	orgId?: string;
	Wrapper?: React.ElementType;
	numberOfTeams?: number;
	userId?: string;
	onUpdateSuccess?: (params: { teamsConnected: TeamData[]; teamsDisconnected: TeamData[] }) => void;
	/**
	 * Optional custom trigger renderer. When provided, replaces the default TeamButtonTrigger.
	 * Receives triggerProps (ref, aria-* attributes for popup) and onClick (analytics handler).
	 * The consumer is responsible for rendering an interactive element that spreads triggerProps
	 * and calls onClick on user interaction.
	 */
	renderTrigger?: (props: RenderTriggerProps) => React.ReactNode;
};

export const TeamButtonComponentContent = ({
	numberOfTeams,
	containerId,
	Wrapper = React.Fragment,
	userId,
	renderTrigger,
}: TeamButtonComponentProps): React.JSX.Element | null => {
	const { fireEvent } = useAnalyticsEvents();
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const [, { setPrincipalUserId }] = usePrincipalUser();

	const { isTeamBiDirectionalEnabled: isExperimentEnabled } = useTeamBiDirectionalExp();

	useEffect(() => {
		if (userId && fg('people-teams-star-teams')) {
			setPrincipalUserId(userId);
		}
	}, [userId, setPrincipalUserId]);

	const handleButtonClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const element = event.currentTarget as HTMLElement;
			const isExpanded = isExperimentEnabled
				? element.getAttribute('aria-expanded') === 'true'
				: true;
			if (isExpanded) {
				const result = analyticsEvent?.context.find(
					(item) => item.attributes && item.attributes.scope !== undefined,
				);

				fireEvent('ui.teamButton.clicked', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: isExperimentEnabled ? 'm2' : 'm1',
				});
			}
		},
		[fireEvent, numberOfTeams, isExperimentEnabled],
	);

	const getTriggerProps = useCallback(
		(triggerProps: TriggerProps) => {
			if (!isExperimentEnabled) {
				return triggerProps;
			}
			const setRef = (node: HTMLButtonElement | null) => {
				triggerRef.current = node;
				if (typeof triggerProps.ref === 'function') {
					triggerProps.ref(node);
				} else if (triggerProps.ref) {
					(triggerProps.ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
				}
			};
			return { ...triggerProps, ref: setRef };
		},
		[isExperimentEnabled],
	);

	const onFocusTrigger = useCallback(() => {
		setTimeout(() => {
			if (triggerRef.current) {
				triggerRef.current.focus();
			}
		}, 0);
	}, [triggerRef]);

	const entryPointPropsMemoized = useMemo(
		() => ({
			// Remove as part of M1 FG cleanup
			...(isExperimentEnabled ? {} : { containerId }),
			onModalCloseComplete: onFocusTrigger,
		}),
		[isExperimentEnabled, containerId, onFocusTrigger],
	);

	const handleRenderTriggerClick = useCallback(() => {
		fireEvent('ui.teamButton.clicked', {
			teamsCount: numberOfTeams ?? 0,
			scope: '',
			version: 'm2',
		});
	}, [fireEvent, numberOfTeams]);

	const triggerMemoized = useCallback(
		(triggerProps: TriggerProps) => {
			const processedTriggerProps = getTriggerProps(triggerProps);

			if (renderTrigger && fg('puma_1588_swap_team_button_with_add_people')) {
				return (
					<Wrapper>
						{renderTrigger({
							triggerProps: processedTriggerProps,
							onClick: handleRenderTriggerClick,
						})}
					</Wrapper>
				);
			}

			return (
				<Wrapper>
					<TeamButtonTrigger
						triggerProps={processedTriggerProps}
						// numberOfTeams will never be undefined/0 as we check for the below.
						numberOfTeams={numberOfTeams ?? 0}
						onClick={handleButtonClick}
					/>
				</Wrapper>
			);
		},
		[Wrapper, getTriggerProps, numberOfTeams, handleButtonClick, renderTrigger, handleRenderTriggerClick],
	);

	if (numberOfTeams === undefined) {
		return null;
	}

	if (numberOfTeams === 0 && !isExperimentEnabled) {
		return null;
	}

	return (
		<FlagsProvider>
			<ModalContextProvider>
				<PopupTrigger
					entryPoint={asyncTeamSelectorEntryPoint}
					entryPointProps={entryPointPropsMemoized}
					entryPointParams={{
						containerId,
						cloudId: getSiteId(containerId, { enableLogging: false }) || '',
					}}
					trigger={triggerMemoized}
					fallback={<TeamSelectorSkeleton />}
					placement="bottom-start"
					strategy="absolute"
					popupComponent={PopupComponent}
				/>
			</ModalContextProvider>
		</FlagsProvider>
	);
};

export const TeamButtonComponent = (props: TeamButtonComponentProps): React.JSX.Element => {
	const { containerId, orgId, onUpdateSuccess } = props;
	const containerType = getContainerType(containerId);
	const isExperimentEnabled = isTeamBiDirectionalEnabled(containerType);

	if (isExperimentEnabled) {
		return (
			<TeamSelectorProvider
				containerId={containerId}
				containerType={containerType}
				// cloudId is guaranteed to be a string as we will always receive a valid containerId
				cloudId={getSiteId(containerId) || ''}
				// orgId will always be available if isTeamBiDirectionalEnabled is true
				orgId={orgId || ''}
				onUpdateSuccess={onUpdateSuccess}
			>
				<TeamButtonComponentContent {...props} />
			</TeamSelectorProvider>
		);
	}

	return <TeamButtonComponentContent {...props} />;
};
